import { API_BASE_URL } from "@/config";
import { validateToken } from "@/utils/auth";
import Swal from "sweetalert2";
import { pdf_decryptify } from "@/utils/pdf_management.js";

export default {
  name: "SupportDocumentation",
  emits: ["validity-change", "previous"],
  props: {
    getSavedSectionResponses: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    // Transaction type from Personal Details: "PRIVATE" or "COMMERCIAL"
    transaction_type: "",
    id_type: "", // "ID Book" or "ID Card"

    // File input refs storage
    file_inputs: {},

    // Seriti document categories - using API category names directly
    selected_files: {
      "ID DOCUMENT - CLIENT": [],
      "DRIVERS LICENCE": [],
      "SALARY SLIP": [],
      "PROOF OF RESIDENCE": [],
      "MARRIAGE CERTIFICATE": [],
      "BANK STATEMENT": [],
      "COMPANY REGISTRATION DOCUMENTS": [],
    },
    existing_documents: {
      "ID DOCUMENT - CLIENT": [],
      "DRIVERS LICENCE": [],
      "SALARY SLIP": [],
      "PROOF OF RESIDENCE": [],
      "MARRIAGE CERTIFICATE": [],
      "BANK STATEMENT": [],
      "COMPANY REGISTRATION DOCUMENTS": [],
    },
    error_messages: {
      "ID DOCUMENT - CLIENT": "",
      "DRIVERS LICENCE": "",
      "SALARY SLIP": "",
      "PROOF OF RESIDENCE": "",
      "MARRIAGE CERTIFICATE": "",
      "BANK STATEMENT": "",
      "COMPANY REGISTRATION DOCUMENTS": "",
    },
    is_dragging: {
      "ID DOCUMENT - CLIENT": false,
      "DRIVERS LICENCE": false,
      "SALARY SLIP": false,
      "PROOF OF RESIDENCE": false,
      "MARRIAGE CERTIFICATE": false,
      "BANK STATEMENT": false,
      "COMPANY REGISTRATION DOCUMENTS": false,
    },
    max_files: {
      "ID DOCUMENT - CLIENT": 2, // Front and back for ID card, or 1 for ID book
      "DRIVERS LICENCE": 2, // Front and back
      "SALARY SLIP": 3, // 3 months
      "PROOF OF RESIDENCE": 1,
      "MARRIAGE CERTIFICATE": 1,
      "BANK STATEMENT": 3, // 3 months
      "COMPANY REGISTRATION DOCUMENTS": 3,
    },
    is_loading: false,
    workflow_id: null,
    is_married: false,
    has_attempted_submit: false,
  }),
  computed: {
    document_keys() {
      return Object.keys(this.selected_files);
    },
    all_files() {
      const combined = {};
      Object.keys(this.selected_files).forEach((key) => {
        const existing = this.existing_documents[key] || [];
        const selected = this.selected_files[key] || [];
        combined[key] = [...existing, ...selected];
      });
      return combined;
    },
    has_any_files() {
      return Object.values(this.all_files).some(
        (files) => files && files.length > 0
      );
    },
    // Determine applicant type based on transaction_type from Personal Details
    applicant_type() {
      return this.transaction_type === "COMMERCIAL" ? "Company" : "Individual";
    },
    all_required_filled() {
      const files = this.all_files;

      // Helper to safely get file count
      const getCount = (key) => (files[key] && files[key].length) || 0;

      // ID Document required (at least 1 file)
      if (getCount("ID DOCUMENT - CLIENT") < 1) {
        return false;
      }

      // Driver's License required (at least 1 file)
      if (getCount("DRIVERS LICENCE") < 1) {
        return false;
      }

      // Bank statements required (at least 1)
      if (getCount("BANK STATEMENT") < 1) {
        return false;
      }

      // Proof of residence required
      if (getCount("PROOF OF RESIDENCE") < 1) {
        return false;
      }

      // For Individual (PRIVATE): Salary slip required
      if (this.transaction_type === "PRIVATE" && getCount("SALARY SLIP") < 1) {
        return false;
      }

      // For Company (COMMERCIAL): Company registration required
      if (
        this.transaction_type === "COMMERCIAL" &&
        getCount("COMPANY REGISTRATION DOCUMENTS") < 1
      ) {
        return false;
      }

      // Marriage certificate required if married (Individual only)
      if (
        this.transaction_type === "PRIVATE" &&
        this.is_married &&
        getCount("MARRIAGE CERTIFICATE") < 1
      ) {
        return false;
      }

      return true;
    },
  },
  methods: {
    pdf_decryptify,
    previous() {
      this.$emit("previous");
    },
    get_files_for_key(key) {
      return this.all_files[key] || [];
    },
    trigger_file_input(key) {
      if (this.file_inputs[key]) {
        this.file_inputs[key].click();
      }
    },
    get_label(doc) {
      const labels = {
        "ID DOCUMENT - CLIENT": "ID Document (ID Book/Card)",
        "DRIVERS LICENCE": "Driver's Licence",
        "SALARY SLIP": "Salary Slip - 3 Months",
        "PROOF OF RESIDENCE": "Proof of Residence",
        "MARRIAGE CERTIFICATE": "Marriage Certificate",
        "BANK STATEMENT": "Bank Statement - 3 Months",
        "COMPANY REGISTRATION DOCUMENTS": "Company Registration Documents",
      };
      return labels[doc] || doc;
    },
    get_description(doc) {
      const descriptions = {
        "ID DOCUMENT - CLIENT": "Upload a copy of your ID book, ID card (front & back).",
        "DRIVERS LICENCE": "Upload front and back of your driver's licence.",
        "PROOF OF RESIDENCE":
          "Please provide proof of address with your full name. Statements only, no invoices.",
        "BANK STATEMENT":
          "Please upload your most recent 3 months bank statements.",
        "SALARY SLIP": "Please upload your most recent 3 months salary slips.",
        "MARRIAGE CERTIFICATE": "Required only if you are married.",
        "COMPANY REGISTRATION DOCUMENTS":
          "Required for commercial/company applications.",
      };
      return descriptions[doc] || "";
    },
    is_required(doc) {
      // ID document always required
      if (doc === "ID DOCUMENT - CLIENT") return true;

      // Driver's license always required
      if (doc === "DRIVERS LICENCE") return true;

      // Bank statements always required
      if (doc === "BANK STATEMENT") return true;

      // Proof of residence always required
      if (doc === "PROOF OF RESIDENCE") return true;

      // Salary slip required for Individual (PRIVATE)
      if (doc === "SALARY SLIP" && this.transaction_type === "PRIVATE")
        return true;

      // Company registration required for Company (COMMERCIAL)
      if (
        doc === "COMPANY REGISTRATION DOCUMENTS" &&
        this.transaction_type === "COMMERCIAL"
      )
        return true;

      // Marriage certificate required if married (Individual only)
      if (
        doc === "MARRIAGE CERTIFICATE" &&
        this.transaction_type === "PRIVATE" &&
        this.is_married
      )
        return true;

      return false;
    },
    should_show_section(doc) {
      // Company registration only for COMMERCIAL
      if (
        doc === "COMPANY REGISTRATION DOCUMENTS" &&
        this.transaction_type !== "COMMERCIAL"
      ) {
        return false;
      }

      // Salary slip only for PRIVATE (Individual)
      if (doc === "SALARY SLIP" && this.transaction_type === "COMMERCIAL") {
        return false;
      }

      // Hide Marriage Certificate if not married or if commercial
      if (doc === "MARRIAGE CERTIFICATE") {
        if (this.transaction_type === "COMMERCIAL" || !this.is_married) {
          return false;
        }
      }

      // Hide Bank Statement upload if Online Bank Statements exist
      if (doc === "BANK STATEMENT") {
        const bankStatements = this.existing_documents["BANK STATEMENT"] || [];
        const hasOnlineStatements = bankStatements.some(
          (file) =>
            file.document_path &&
            file.document_path.includes("Online Bank Statements")
        );

        if (hasOnlineStatements) {
          return false;
        }
      }

      return true;
    },
    async create_file_from_base_64(base64String, fileName, documentType) {
      try {
        if (!base64String) {
          console.warn("Base64 string is null or undefined for file:", fileName);
          return null;
        }
        const byteString = atob(base64String.split(",")[1] || base64String);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        let type = "application/octet-stream";
        if (fileName.endsWith(".pdf")) type = "application/pdf";
        else if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg"))
          type = "image/jpeg";
        else if (fileName.endsWith(".png")) type = "image/png";

        const file = new File([ab], fileName, {
          type: type,
          lastModified: Date.now(),
        });
        file.document_id = documentType.document_id;
        file.is_existing = true;
        file.document_url = null;
        return file;
      } catch (error) {
        console.error("Error creating file from base64:", error);
        return null;
      }
    },
    async fetch_documents() {
      const token = localStorage.getItem("token");
      const upload_api = `${API_BASE_URL}/verify_api/financial_information_api/seriti_get_uploaded_documents/`;

      Swal.fire({
        title: "Loading documents",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await $.ajax({
          type: "POST",
          url: upload_api,
          headers: { Authorization: `Token ${token}` },
          data: JSON.stringify({
            workflow_id: this.workflow_id,
          }),
          contentType: "application/json",
        });

        Swal.close();

        if (response.status === "success" && response.data) {
          const documents = Array.isArray(response.data) ? response.data : [];

          // Reset existing documents
          Object.keys(this.existing_documents).forEach((key) => {
            this.existing_documents[key] = [];
          });

          // Process each document
          for (const doc of documents) {
            const documentCategory = doc.document_type;

            // Extract filename from URL
            const urlParts = doc.document_path.split("/");
            const fileName =
              urlParts[urlParts.length - 1].split("?")[0] ||
              `${documentCategory.toLowerCase()}_document`;

            // Create file object from base64 string
            const fileObj = await this.create_file_from_base_64(
              doc.document_base_64,
              fileName,
              doc
            );

            // Ensure document_path is preserved for validation logic
            if (fileObj) {
              fileObj.document_path = doc.document_path;
            }

            if (fileObj && this.existing_documents[documentCategory]) {
              this.existing_documents[documentCategory].push(fileObj);
            }
          }
        }
      } catch (error) {
        Swal.close();
        console.error("Error fetching documents:", error);
      }
    },
    async fetch_transaction_type() {
      // Get transaction type (client_category) from saved Personal Details section
      try {
        const responses = await this.getSavedSectionResponses(
          "Personal Details"
        );
        if (Array.isArray(responses) && responses.length > 0) {
          // Look for client_category field
          const categoryAnswer = responses.find(
            (r) =>
              r.question_text &&
              (r.question_text.toLowerCase().includes("transaction") ||
                r.question_text.toLowerCase().includes("client category"))
          );
          if (categoryAnswer && categoryAnswer.answer) {
            this.transaction_type = categoryAnswer.answer.toUpperCase();
          } else {
            // Default to PRIVATE if not found
            this.transaction_type = "PRIVATE";
          }
        } else {
          this.transaction_type = "PRIVATE";
        }
      } catch (error) {
        console.error("Error fetching transaction type:", error);
        this.transaction_type = "PRIVATE";
      }
    },
    async fetch_marital_status() {
      // Get marital status from saved Marital Information section
      try {
        const responses = await this.getSavedSectionResponses(
          "Marital Information"
        );
        if (Array.isArray(responses) && responses.length > 0) {
          const maritalStatusAnswer = responses.find(
            (r) =>
              r.question_text &&
              r.question_text.toLowerCase().includes("marital status")
          );
          if (maritalStatusAnswer && maritalStatusAnswer.answer) {
            this.is_married =
              maritalStatusAnswer.answer.toLowerCase() === "married";
          }
        }
      } catch (error) {
        console.error("Error fetching marital status:", error);
      }
    },
    async handle_file_change(event, type) {
      if (event.target.files && event.target.files.length > 0) {
        let files_returned = Array.from(event.target.files);
        const processed_files = [];

        for (const file of files_returned) {
          try {
            if (file.type === "application/pdf") {
              const result = await this.pdf_decryptify(file);
              if (!result || typeof result === "string" || !result.type) {
                continue;
              }
              processed_files.push(result);
            } else {
              processed_files.push(file);
            }
          } catch (err) {
            console.error("Error processing file:", err);
            continue;
          }
        }

        event.target.value = "";

        let new_files = processed_files;
        if (new_files.length === 0) {
          return;
        }

        const current_total = this.all_files[type].length;

        if (current_total + new_files.length > this.max_files[type]) {
          this.error_messages[
            type
          ] = `You can upload a maximum of ${this.max_files[type]} files`;
          return;
        }

        const valid_types = [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
        ];
        const invalid_files = new_files.filter(
          (file) => !valid_types.includes(file.type)
        );
        if (invalid_files.length > 0) {
          this.error_messages[type] =
            "Some files have invalid types. Please upload only PDF, JPEG, PNG, or JPG files.";
          return;
        }

        const oversized = new_files.filter(
          (file) => file.size > 50 * 1024 * 1024
        );
        if (oversized.length > 0) {
          this.error_messages[type] =
            "Some files exceed the maximum size limit of 50MB.";
          return;
        }

        this.selected_files[type] = [
          ...this.selected_files[type],
          ...new_files,
        ];
        this.error_messages[type] = "";
      }
    },
    async handle_file_drop(event, type) {
      this.is_dragging[type] = false;
      const files = event.dataTransfer.files;

      if (files && files.length > 0) {
        let files_returned = Array.from(files);
        const processed_files = [];

        for (const file of files_returned) {
          try {
            if (file.type === "application/pdf") {
              const result = await this.pdf_decryptify(file);
              if (!result || typeof result === "string" || !result.type) {
                continue;
              }
              processed_files.push(result);
            } else {
              processed_files.push(file);
            }
          } catch (err) {
            console.error("Error processing file:", err);
            continue;
          }
        }

        let new_files = processed_files;
        if (new_files.length === 0) {
          return;
        }

        const current_total = this.all_files[type].length;

        if (current_total + new_files.length > this.max_files[type]) {
          this.error_messages[
            type
          ] = `You can upload a maximum of ${this.max_files[type]} files`;
          return;
        }

        const valid_types = [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
        ];
        const valid_files = new_files.filter((file) =>
          valid_types.includes(file.type)
        );
        if (valid_files.length === 0) {
          this.error_messages[type] =
            "Invalid file type(s). Please upload PDF, JPEG, PNG, or JPG files.";
          return;
        }

        const valid_sized = valid_files.filter(
          (file) => file.size <= 50 * 1024 * 1024
        );
        if (valid_sized.length === 0) {
          this.error_messages[type] =
            "All files exceed the maximum size limit of 50MB.";
          return;
        }

        if (valid_sized.length !== new_files.length) {
          this.error_messages[type] =
            "Some files were skipped because they exceed the 50MB size limit or invalid type.";
        }

        this.selected_files[type] = [
          ...this.selected_files[type],
          ...valid_sized,
        ];
      }
    },
    remove_file(type, index) {
      const allFiles = this.all_files[type];
      const fileToRemove = allFiles[index];

      if (fileToRemove.is_existing) {
        const existingIndex = this.existing_documents[type].findIndex(
          (f) => f.document_id === fileToRemove.document_id
        );
        if (existingIndex > -1) {
          this.existing_documents[type].splice(existingIndex, 1);
        }
      } else {
        const selectedIndex = index - this.existing_documents[type].length;
        if (selectedIndex >= 0) {
          this.selected_files[type].splice(selectedIndex, 1);
        }
      }
    },
    view_file(file) {
      if (file.is_existing && file.document_url) {
        window.open(file.document_url, "_blank");
      } else {
        const url = URL.createObjectURL(file);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    },
    format_file_size(bytes) {
      if (bytes < 1024) return bytes + " bytes";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
      return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    },
    prepare_files_for_api() {
      // Map Seriti document categories to API field names
      const prepared_files = {};

      const category_to_field = {
        "ID DOCUMENT - CLIENT": "id_document",
        "DRIVERS LICENCE": "drivers_licence",
        "SALARY SLIP": "salary_slip",
        "PROOF OF RESIDENCE": "proof_of_residence",
        "MARRIAGE CERTIFICATE": "marriage_certificate",
        "BANK STATEMENT": "bank_statement",
        "COMPANY REGISTRATION DOCUMENTS": "company_registration",
      };

      Object.entries(category_to_field).forEach(([category, field]) => {
        const files = this.all_files[category] || [];
        if (files.length > 0) {
          prepared_files[field] = files.filter((f) => !f.is_existing);
        }
      });

      return prepared_files;
    },
    async upload_documents() {
      this.has_attempted_submit = true;

      if (!this.all_required_filled) {
        Swal.fire({
          icon: "warning",
          title: "Validation",
          text: "Please upload all required documents",
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // Check total file size before sending
      const allFiles = Object.values(this.all_files).flat();
      const totalSizeBytes = allFiles.reduce((sum, file) => sum + file.size, 0);
      const maxSizeBytes = 50 * 1024 * 1024;

      if (totalSizeBytes > maxSizeBytes) {
        Swal.fire({
          icon: "error",
          title: "Upload Limit Exceeded",
          text: "Total file size exceeds 50MB. Please remove some files.",
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      this.is_loading = true;

      Swal.fire({
        title: "Saving documents",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const token = localStorage.getItem("token");
      const upload_api = `${API_BASE_URL}/verify_api/financial_information_api/seriti_upload_supporting_documentation/`;
      const formData = new FormData();

      const prepared_files = this.prepare_files_for_api();

      Object.entries(prepared_files).forEach(([key, files]) => {
        if (Array.isArray(files)) {
          files.forEach((file) => {
            formData.append(key, file);
          });
        }
      });

      formData.append("workflow_id", this.workflow_id);

      try {
        await $.ajax({
          url: upload_api,
          method: "POST",
          contentType: false,
          processData: false,
          headers: { Authorization: `Token ${token}` },
          data: formData,
        });

        Swal.close();
        this.is_loading = false;

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Documents uploaded successfully!",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });


        // Emit validity change first to trigger application submission
        this.$emit("validity-change", true);
        
      } catch (error) {
        Swal.close();
        this.is_loading = false;

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.responseJSON?.message ||
            "Upload failed. Please try again later.",
          timer: 3000,
          timerProgressBar: true,
        });
      }
    },
  },
  mounted() {
    if (!validateToken(this.$router)) return;
    this.workflow_id = this.$store.getters.get_workflow_id;
    this.$nextTick(async () => {
      await this.fetch_transaction_type();
      await this.fetch_marital_status();
      await this.fetch_documents();
    });
  },
};
